import React, { useState } from 'react';
import { Button, message, Modal, List, Typography } from 'antd';
import { HeartOutlined, PlusOutlined } from '@ant-design/icons';
import { useAuth } from '../user/auth/AuthContext';
import { useWatchlist } from '../context/WatchlistContext';
import { API_CONFIG, apiClient } from '../api/config';
import { logger } from '../utils/logger';

/**
 * 共用的表格列定義
 * 用於 WatchlistPage 和 StockScreener 組件
 */

// Watchlist操作組件
const WatchlistAction = ({ record, onAddToWatchlist }) => {
  const { isAuthenticated, user } = useAuth();
  const { watchlistGroups } = useWatchlist();
  const [loading, setLoading] = useState(false);
  const [showGroupModal, setShowGroupModal] = useState(false);

  // 處理加入watchlist
  const handleAddToWatchlist = async (groupName = 'default') => {
    if (!isAuthenticated || !user) {
      message.warning('請先登錄以使用收藏功能');
      return;
    }

    if (!record || !record.company_symbol) {
      message.error('無效的股票代碼');
      return;
    }

    try {
      setLoading(true);
      await apiClient.post(API_CONFIG.endpoints.watchlist.add, {
        symbol: record.company_symbol,
        group: groupName,
        user_id: user.user_id
      });

      message.success(`已將 ${record.company_symbol} 添加到 ${groupName} 分組`);
      setShowGroupModal(false);
      
      // 調用回調函數（如果提供）
      if (onAddToWatchlist) {
        onAddToWatchlist(record, groupName);
      }
    } catch (err) {
      logger.error('添加股票到watchlist失敗:', err);
      const errorMsg = err.response?.data?.message || err.message || '添加失敗';
      message.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // 顯示分組選擇模態框
  const showGroupSelection = () => {
    if (!isAuthenticated || !user) {
      message.warning('請先登錄以使用收藏功能');
      return;
    }
    setShowGroupModal(true);
  };

  if (!isAuthenticated || !user) {
    return (
      <Button
        icon={<HeartOutlined />}
        type="text"
        size="small"
        disabled
        title="請先登錄"
        style={{ color: '#ccc' }}
      />
    );
  }

  return (
    <>
      <Button
        icon={<HeartOutlined />}
        type="text"
        size="small"
        onClick={showGroupSelection}
        loading={loading}
        title="加入收藏列表"
        style={{ color: '#ff4d4f' }}
      />
      
      <Modal
        title={`將 ${record?.company_symbol} 加入收藏列表`}
        open={showGroupModal}
        onCancel={() => setShowGroupModal(false)}
        footer={null}
        width={400}
      >
        <List
          dataSource={watchlistGroups}
          renderItem={(group) => (
            <List.Item
              actions={[
                <Button
                  key="add"
                  type="primary"
                  size="small"
                  onClick={() => handleAddToWatchlist(group.group)}
                  loading={loading}
                >
                  加入
                </Button>
              ]}
            >
              <List.Item.Meta
                title={group.group}

              />
            </List.Item>
          )}
        />
        
        {watchlistGroups.length === 0 && (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <Typography.Text type="secondary">
              您還沒有創建任何收藏列表
            </Typography.Text>
            <br />
            <Button
              type="dashed"
              icon={<PlusOutlined />}
              onClick={() => handleAddToWatchlist('default')}
              loading={loading}
              style={{ marginTop: '10px' }}
            >
              創建默認列表並加入
            </Button>
          </div>
        )}
      </Modal>
    </>
  );
};

// Watchlist操作列
export const watchlistActionColumn = {
  title: () => <span style={{ color: 'white' }}>收藏</span>,
  key: 'watchlist_action',
  width: 60,
  fixed: 'left',
  className: 'icon-column',
  render: (text, record) => (
    <WatchlistAction 
      record={record} 
      onAddToWatchlist={(record, groupName) => {
        logger.log(`股票 ${record.company_symbol} 已添加到 ${groupName} 分組`);
      }}
    />
  )
};

// 基礎列定義 - 公司信息
export const baseColumns = [
  {
    title: () => <span style={{ color: 'white' }}>公司</span>,
    dataIndex: 'company_symbol',
    key: 'company_symbol',
    width: 90,
    fixed: 'left',
    render: (text, record) => {
      // 防護措施：確保 record 和 text 不為 null/undefined
      if (!record || !text) {
        return <span style={{ color: '#999' }}>N/A</span>;
      }
      
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ 
            width: '24px', 
            height: '24px', 
            overflow: 'hidden',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: '#f0f0f0',
            borderRadius: '4px',
            flexShrink: 0
          }}>
            {record.image ? (
              <img 
                src={record.image} 
                alt={text} 
                style={{ 
                  width: '100%', 
                  height: '100%', 
                  objectFit: 'contain' 
                }}
                onError={(e) => {
                  if (e.target) {
                    e.target.style.display = 'none';
                  }
                }}
              />
            ) : (
              <span style={{ fontSize: '12px', color: '#999' }}>
                {text ? text[0] : 'N/A'}
              </span>
            )}
          </div>
          <span style={{ fontWeight: 'bold', fontSize: '13px' }}>{text}</span>
        </div>
      );
    }
  }
];

// 基本信息列 - 包含公司名稱、產業、細分產業
export const basicInfoColumns = [
  watchlistActionColumn,
  ...baseColumns,
  {
    title: () => <span style={{ color: 'white' }}>公司名稱</span>,
    dataIndex: 'company_name',
    key: 'company_name',
    width: 150,
    ellipsis: true
  },
  {
    title: () => <span style={{ color: 'white' }}>產業</span>,
    dataIndex: 'sector',
    key: 'sector',
    width: 140,
    ellipsis: true
  },
  {
    title: () => <span style={{ color: 'white' }}>類別</span>,
    dataIndex: 'industry',
    key: 'industry',
    width: 140,
    ellipsis: true
  },
  {
    title: () => <span style={{ color: 'white' }}>財報發布日</span>,
    dataIndex: 'earningsAnnouncement',
    key: 'earningsAnnouncement',
    width: 110,
    render: (text) => text ? new Date(text).toLocaleDateString() : '-'
  },
  {
    title: () => <span style={{ color: 'white' }}>市值</span>,
    dataIndex: 'marketCap',
    key: 'marketCap',
    width: 100,
    render: (value) => value ? `$${(value / 1000000000).toFixed(2)}B` : '-'
  },
  {
    title: () => <span style={{ color: 'white' }}>PE</span>,
    dataIndex: 'pe',
    key: 'pe',
    width: 70,
    render: (value) => value ? parseFloat(value).toFixed(2) : '-'
  },
  {
    title: () => <span style={{ color: 'white' }}>股價</span>,
    dataIndex: 'price',
    key: 'price',
    width: 80,
    render: (value) => value ? `$${parseFloat(value).toFixed(2)}` : '-'
  },
  {
    title: () => <span style={{ color: 'white' }}>漲跌幅</span>,
    dataIndex: 'changesPercentage',
    key: 'changesPercentage',
    width: 80,
    render: (value) => {
      if (!value) return '-';
      const numValue = parseFloat(value);
      const color = numValue >= 0 ? '#52c41a' : '#ff4d4f';
      return <span style={{ color }}>{numValue >= 0 ? '+' : ''}{numValue.toFixed(2)}%</span>;
    }
  },
  {
    title: () => <span style={{ color: 'white' }}>目標價</span>,
    dataIndex: 'targetConsensus',
    key: 'targetConsensus',
    width: 80,
    render: (value) => value ? `$${parseFloat(value).toFixed(2)}` : '-'
  },
  {
    title: () => <span style={{ color: 'white' }}>獲利空間</span>,
    dataIndex: 'price_diff_percentage',
    key: 'price_diff_percentage',
    width: 80,
    render: (value) => {
      if (!value) return '-';
      const numValue = parseFloat(value);
      const color = numValue >= 0 ? '#52c41a' : '#ff4d4f';
      return <span style={{ color }}>{numValue >= 0 ? '+' : ''}{numValue.toFixed(2)}%</span>;
    }
  },
  {
    title: () => <span style={{ color: 'white' }}>成交量</span>,
    dataIndex: 'volume',
    key: 'volume',
    width: 100,
    render: (value) => value ? `${(value / 1000000).toFixed(2)}M` : '-'
  }
];

// 成長與估值列
export const growthValuationColumns = [
  watchlistActionColumn,
  ...baseColumns,
  {
    title: () => <span style={{ color: 'white' }}>市值</span>,
    dataIndex: 'marketCap',
    key: 'marketCap',
    width: 100,
    render: (value) => value ? `$${(value / 1000000000).toFixed(2)}B` : '-'
  },
  {
    title: () => <span style={{ color: 'white' }}>P/E</span>,
    dataIndex: 'priceToEarningsRatioTTM',
    key: 'priceToEarningsRatioTTM',
    width: 80,
    render: (value) => value ? parseFloat(value).toFixed(2) : '-'
  },
  {
    title: () => <span style={{ color: 'white' }}>PEG</span>,
    dataIndex: 'forwardPriceToEarningsGrowthRatioTTM',
    key: 'forwardPriceToEarningsGrowthRatioTTM',
    width: 80,
    render: (value) => value ? parseFloat(value).toFixed(2) : '-'
  },
  {
    title: () => <span style={{ color: 'white' }}>P/S</span>,
    dataIndex: 'priceToSalesRatioTTM',
    key: 'priceToSalesRatioTTM',
    width: 80,
    render: (value) => value ? parseFloat(value).toFixed(2) : '-'
  },
  {
    title: () => <span style={{ color: 'white' }}>P/B</span>,
    dataIndex: 'priceToBookRatioTTM',
    key: 'priceToBookRatioTTM',
    width: 80,
    render: (value) => value ? parseFloat(value).toFixed(2) : '-'
  },
  {
    title: () => <span style={{ color: 'white' }}>未來一年營收</span>,
    dataIndex: 'revenue_growth_1y_percent',
    key: 'revenue_growth_1y_percent',
    width: 100,
    render: (value) => {
      if (!value) return '-';
      const numValue = parseFloat(value);
      const color = numValue >= 0 ? '#52c41a' : '#ff4d4f';
      return <span style={{ color }}>{numValue >= 0 ? '+' : ''}{numValue.toFixed(2)}%</span>;
    }
  },
  {
    title: () => <span style={{ color: 'white' }}>未來三年營收</span>,
    dataIndex: 'revenue_growth_3y_percent',
    key: 'revenue_growth_3y_percent',
    width: 100,
    render: (value) => {
      if (!value) return '-';
      const numValue = parseFloat(value);
      const color = numValue >= 0 ? '#52c41a' : '#ff4d4f';
      return <span style={{ color }}>{numValue >= 0 ? '+' : ''}{numValue.toFixed(2)}%</span>;
    }
  },
  {
    title: () => <span style={{ color: 'white' }}>未來一年EPS</span>,
    dataIndex: 'eps_growth_1y_percent',
    key: 'eps_growth_1y_percent',
    width: 100,
    render: (value) => {
      if (!value) return '-';
      const numValue = parseFloat(value);
      const color = numValue >= 0 ? '#52c41a' : '#ff4d4f';
      return <span style={{ color }}>{numValue >= 0 ? '+' : ''}{numValue.toFixed(2)}%</span>;
    }
  },
  {
    title: () => <span style={{ color: 'white' }}>未來三年EPS</span>,
    dataIndex: 'eps_growth_3y_percent',
    key: 'eps_growth_3y_percent',
    width: 100,
    render: (value) => {
      if (!value) return '-';
      const numValue = parseFloat(value);
      const color = numValue >= 0 ? '#52c41a' : '#ff4d4f';
      return <span style={{ color }}>{numValue >= 0 ? '+' : ''}{numValue.toFixed(2)}%</span>;
    }
  },
  {
    title: () => <span style={{ color: 'white' }}>過去一年營收</span>,
    dataIndex: 'revenue_growth_1y',
    key: 'revenue_growth_1y',
    width: 100,
    render: (value) => {
      if (!value) return '-';
      const numValue = parseFloat(value);
      const color = numValue >= 0 ? '#52c41a' : '#ff4d4f';
      return <span style={{ color }}>{numValue >= 0 ? '+' : ''}{numValue.toFixed(2)}%</span>;
    }
  },
  {
    title: () => <span style={{ color: 'white' }}>過去三年營收</span>,
    dataIndex: 'revenue_growth_3y',
    key: 'revenue_growth_3y',
    width: 100,
    render: (value) => {
      if (!value) return '-';
      const numValue = parseFloat(value);
      const color = numValue >= 0 ? '#52c41a' : '#ff4d4f';
      return <span style={{ color }}>{numValue >= 0 ? '+' : ''}{numValue.toFixed(2)}%</span>;
    }
  },
  {
    title: () => <span style={{ color: 'white' }}>過去一年EPS</span>,
    dataIndex: 'eps_growth_1y',
    key: 'eps_growth_1y',
    width: 100,
    render: (value) => {
      if (!value) return '-';
      const numValue = parseFloat(value);
      const color = numValue >= 0 ? '#52c41a' : '#ff4d4f';
      return <span style={{ color }}>{numValue >= 0 ? '+' : ''}{numValue.toFixed(2)}%</span>;
    }
  },
  {
    title: () => <span style={{ color: 'white' }}>過去三年EPS</span>,
    dataIndex: 'eps_growth_3y',
    key: 'eps_growth_3y',
    width: 100,
    render: (value) => {
      if (!value) return '-';
      const numValue = parseFloat(value);
      const color = numValue >= 0 ? '#52c41a' : '#ff4d4f';
      return <span style={{ color }}>{numValue >= 0 ? '+' : ''}{numValue.toFixed(2)}%</span>;
    }
  }
];

// 財務比率列
export const financialRatiosColumns = [
  watchlistActionColumn,
  ...baseColumns,
  {
    title: () => <span style={{ color: 'white' }}>市值</span>,
    dataIndex: 'marketCap',
    key: 'marketCap',
    width: 100,
    render: (value) => value ? `$${(value / 1000000000).toFixed(2)}B` : '-'
  },
  {
    title: () => <span style={{ color: 'white' }}>毛利率</span>,
    dataIndex: 'grossProfitMargin',
    key: 'grossProfitMargin',
    width: 80,
    render: (value) => value ? `${(value * 100).toFixed(2)}%` : '-'
  },
  {
    title: () => <span style={{ color: 'white' }}>淨利率</span>,
    dataIndex: 'netProfitMargin',
    key: 'netProfitMargin',
    width: 80,
    render: (value) => value ? `${(value * 100).toFixed(2)}%` : '-'
  },
  {
    title: () => <span style={{ color: 'white' }}>股息率</span>,
    dataIndex: 'dividendYieldPercentage',
    key: 'dividendYieldPercentage',
    width: 80,
    render: (value) => value ? `${(value * 100).toFixed(2)}%` : '-'
  },
  {
    title: () => <span style={{ color: 'white' }}>ROA</span>,
    dataIndex: 'returnOnAssets',
    key: 'returnOnAssets',
    width: 80,
    render: (value) => value ? `${(value * 100).toFixed(2)}%` : '-'
  },
  {
    title: () => <span style={{ color: 'white' }}>ROE</span>,
    dataIndex: 'returnOnEquity',
    key: 'returnOnEquity',
    width: 80,
    render: (value) => value ? `${(value * 100).toFixed(2)}%` : '-'
  },
  {
    title: () => <span style={{ color: 'white' }}>流動比</span>,
    dataIndex: 'currentRatio',
    key: 'currentRatio',
    width: 80,
    render: (value) => value ? parseFloat(value).toFixed(2) : '-'
  },
  {
    title: () => <span style={{ color: 'white' }}>速動比</span>,
    dataIndex: 'quickRatio',
    key: 'quickRatio',
    width: 80,
    render: (value) => value ? parseFloat(value).toFixed(2) : '-'
  },
  {
    title: () => <span style={{ color: 'white' }}>庫存週轉比</span>,
    dataIndex: 'inventoryTurnover',
    key: 'inventoryTurnover',
    width: 100,
    render: (value) => value ? parseFloat(value).toFixed(2) : '-'
  }
];

// 價格表現列
export const pricePerformanceColumns = [
  watchlistActionColumn,
  ...baseColumns,
  {
    title: () => <span style={{ color: 'white' }}>市值</span>,
    dataIndex: 'marketCap',
    key: 'marketCap',
    width: 100,
    render: (value) => value ? `$${(value / 1000000000).toFixed(2)}B` : '-'
  },
  {
    title: () => <span style={{ color: 'white' }}>股價</span>,
    dataIndex: 'price',
    key: 'price',
    width: 80,
    render: (value) => value ? `$${parseFloat(value).toFixed(2)}` : '-'
  },
  {
    title: () => <span style={{ color: 'white' }}>漲跌幅</span>,
    dataIndex: 'changesPercentage',
    key: 'changesPercentage',
    width: 80,
    render: (value) => {
      if (!value) return '-';
      const numValue = parseFloat(value);
      const color = numValue >= 0 ? '#52c41a' : '#ff4d4f';
      return <span style={{ color }}>{numValue >= 0 ? '+' : ''}{numValue.toFixed(2)}%</span>;
    }
  },
  {
    title: () => <span style={{ color: 'white' }}>目標價</span>,
    dataIndex: 'targetConsensus',
    key: 'targetConsensus',
    width: 80,
    render: (value) => value ? `$${parseFloat(value).toFixed(2)}` : '-'
  },
  {
    title: () => <span style={{ color: 'white' }}>獲利空間</span>,
    dataIndex: 'price_diff_percentage',
    key: 'price_diff_percentage',
    width: 80,
    render: (value) => {
      if (!value) return '-';
      const numValue = parseFloat(value);
      const color = numValue >= 0 ? '#52c41a' : '#ff4d4f';
      return <span style={{ color }}>{numValue >= 0 ? '+' : ''}{numValue.toFixed(2)}%</span>;
    }
  },
  {
    title: () => <span style={{ color: 'white' }}>成交量</span>,
    dataIndex: 'volume',
    key: 'volume',
    width: 100,
    render: (value) => value ? `${(value / 1000000).toFixed(2)}M` : '-'
  },
  {
    title: () => <span style={{ color: 'white' }}>5D漲幅</span>,
    dataIndex: 'perf_5D',
    key: 'perf_5D',
    width: 80,
    render: (value) => {
      if (!value) return '-';
      const numValue = parseFloat(value);
      const color = numValue >= 0 ? '#52c41a' : '#ff4d4f';
      return <span style={{ color }}>{numValue >= 0 ? '+' : ''}{numValue.toFixed(2)}%</span>;
    }
  },
  {
    title: () => <span style={{ color: 'white' }}>1M漲幅</span>,
    dataIndex: 'perf_1M',
    key: 'perf_1M',
    width: 80,
    render: (value) => {
      if (!value) return '-';
      const numValue = parseFloat(value);
      const color = numValue >= 0 ? '#52c41a' : '#ff4d4f';
      return <span style={{ color }}>{numValue >= 0 ? '+' : ''}{numValue.toFixed(2)}%</span>;
    }
  },
  {
    title: () => <span style={{ color: 'white' }}>3M漲幅</span>,
    dataIndex: 'perf_3M',
    key: 'perf_3M',
    width: 80,
    render: (value) => {
      if (!value) return '-';
      const numValue = parseFloat(value);
      const color = numValue >= 0 ? '#52c41a' : '#ff4d4f';
      return <span style={{ color }}>{numValue >= 0 ? '+' : ''}{numValue.toFixed(2)}%</span>;
    }
  },
  {
    title: () => <span style={{ color: 'white' }}>6M漲幅</span>,
    dataIndex: 'perf_6M',
    key: 'perf_6M',
    width: 80,
    render: (value) => {
      if (!value) return '-';
      const numValue = parseFloat(value);
      const color = numValue >= 0 ? '#52c41a' : '#ff4d4f';
      return <span style={{ color }}>{numValue >= 0 ? '+' : ''}{numValue.toFixed(2)}%</span>;
    }
  },
  {
    title: () => <span style={{ color: 'white' }}>1Y漲幅</span>,
    dataIndex: 'perf_1Y',
    key: 'perf_1Y',
    width: 80,
    render: (value) => {
      if (!value) return '-';
      const numValue = parseFloat(value);
      const color = numValue >= 0 ? '#52c41a' : '#ff4d4f';
      return <span style={{ color }}>{numValue >= 0 ? '+' : ''}{numValue.toFixed(2)}%</span>;
    }
  },
  {
    title: () => <span style={{ color: 'white' }}>50MA</span>,
    dataIndex: 'priceAvg50',
    key: 'priceAvg50',
    width: 80,
    render: (value) => value ? `$${parseFloat(value).toFixed(2)}` : '-'
  },
  {
    title: () => <span style={{ color: 'white' }}>100MA</span>,
    dataIndex: 'priceAvg100',
    key: 'priceAvg100',
    width: 80,
    render: (value) => value ? `$${parseFloat(value).toFixed(2)}` : '-'
  },
  {
    title: () => <span style={{ color: 'white' }}>200MA</span>,
    dataIndex: 'priceAvg200',
    key: 'priceAvg200',
    width: 80,
    render: (value) => value ? `$${parseFloat(value).toFixed(2)}` : '-'
  },
  {
    title: () => <span style={{ color: 'white' }}>年高</span>,
    dataIndex: 'yearHigh',
    key: 'yearHigh',
    width: 80,
    render: (value) => value ? `$${parseFloat(value).toFixed(2)}` : '-'
  },
  {
    title: () => <span style={{ color: 'white' }}>年低</span>,
    dataIndex: 'yearLow',
    key: 'yearLow',
    width: 80,
    render: (value) => value ? `$${parseFloat(value).toFixed(2)}` : '-'
  }
];

// 趨勢變化列
export const trendColumns = [
  watchlistActionColumn,
  ...baseColumns,
  {
    title: () => <span style={{ color: 'white' }}>市值</span>,
    dataIndex: 'marketCap',
    key: 'marketCap',
    width: 100,
    render: (value) => value ? `$${(value / 1000000000).toFixed(2)}B` : '-'
  },
  {
    title: () => <span style={{ color: 'white' }}>平均毛利率</span>,
    dataIndex: 'gpm_curavg',
    key: 'gpm_curavg',
    width: 110,
    render: (value) => value ? `${(parseFloat(value) * 100).toFixed(2)}%` : '-'
  },
  {
    title: () => <span style={{ color: 'white' }}>過去平均毛利率</span>,
    dataIndex: 'gpm_pasavg',
    key: 'gpm_pasavg',
    width: 130,
    render: (value) => value ? `${(parseFloat(value) * 100).toFixed(2)}%` : '-'
  },
  {
    title: () => <span style={{ color: 'white' }}>最新毛利率</span>,
    dataIndex: 'gpm1',
    key: 'gpm1',
    width: 100,
    render: (value) => value ? `${(parseFloat(value) * 100).toFixed(2)}%` : '-'
  },
  {
    title: () => <span style={{ color: 'white' }}>一季前毛利率</span>,
    dataIndex: 'gpm2',
    key: 'gpm2',
    width: 100,
    render: (value) => value ? `${(parseFloat(value) * 100).toFixed(2)}%` : '-'
  },
  {
    title: () => <span style={{ color: 'white' }}>兩季前毛利率</span>,
    dataIndex: 'gpm3',
    key: 'gpm3',
    width: 100,
    render: (value) => value ? `${(parseFloat(value) * 100).toFixed(2)}%` : '-'
  },
  {
    title: () => <span style={{ color: 'white' }}>三季前毛利率</span>,
    dataIndex: 'gpm4',
    key: 'gpm4',
    width: 100,
    render: (value) => value ? `${(parseFloat(value) * 100).toFixed(2)}%` : '-'
  },
  {
    title: () => <span style={{ color: 'white' }}>毛利率趨勢</span>,
    key: 'gpm_trend',
    width: 120,
    render: (text, record) => {
      const gpm1 = record.gpm1;
      const gpm2 = record.gpm2;
      const gpm3 = record.gpm3;
      const gpm4 = record.gpm4;
      
      if (!gpm1 || !gpm2 || !gpm3 || !gpm4) return '-';
      
      const gpmIsRising = gpm4 < gpm3 && gpm3 < gpm2 && gpm2 < gpm1;
      const gpmIsFalling = gpm4 > gpm3 && gpm3 > gpm2 && gpm2 > gpm1;
      
      if (gpmIsRising) {
        return <span style={{ color: '#52c41a', fontWeight: 'bold' }}>↗ 連續上升</span>;
      } else if (gpmIsFalling) {
        return <span style={{ color: '#f5222d', fontWeight: 'bold' }}>↘ 連續下降</span>;
      } else {
        return <span style={{ color: '#faad14', fontWeight: 'bold' }}>→ 非連續</span>;
      }
    }
  },
  {
    title: () => <span style={{ color: 'white' }}>平均營利率</span>,
    dataIndex: 'opm_curavg',
    key: 'opm_curavg',
    width: 110,
    render: (value) => value ? `${(parseFloat(value) * 100).toFixed(2)}%` : '-'
  },
  {
    title: () => <span style={{ color: 'white' }}>過去平均營利率</span>,
    dataIndex: 'opm_pasavg',
    key: 'opm_pasavg',
    width: 130,
    render: (value) => value ? `${(parseFloat(value) * 100).toFixed(2)}%` : '-'
  },
  {
    title: () => <span style={{ color: 'white' }}>最新營利率</span>,
    dataIndex: 'opm1',
    key: 'opm1',
    width: 100,
    render: (value) => value ? `${(parseFloat(value) * 100).toFixed(2)}%` : '-'
  },
  {
    title: () => <span style={{ color: 'white' }}>一季前營利率</span>,
    dataIndex: 'opm2',
    key: 'opm2',
    width: 110,
    render: (value) => value ? `${(parseFloat(value) * 100).toFixed(2)}%` : '-'
  },
  {
    title: () => <span style={{ color: 'white' }}>兩季前營利率</span>,
    dataIndex: 'opm3',
    key: 'opm3',
    width: 110,
    render: (value) => value ? `${(parseFloat(value) * 100).toFixed(2)}%` : '-'
  },
  {
    title: () => <span style={{ color: 'white' }}>三季前營利率</span>,
    dataIndex: 'opm4',
    key: 'opm4',
    width: 110,
    render: (value) => value ? `${(parseFloat(value) * 100).toFixed(2)}%` : '-'
  },
  {
    title: () => <span style={{ color: 'white' }}>營利率趨勢</span>,
    key: 'opm_trend',
    width: 120,
    render: (text, record) => {
      const opm1 = record.opm1;
      const opm2 = record.opm2;
      const opm3 = record.opm3;
      const opm4 = record.opm4;
      
      if (!opm1 || !opm2 || !opm3 || !opm4) return '-';
      
      const opmIsRising = opm4 < opm3 && opm3 < opm2 && opm2 < opm1;
      const opmIsFalling = opm4 > opm3 && opm3 > opm2 && opm2 > opm1;
      
      if (opmIsRising) {
        return <span style={{ color: '#52c41a', fontWeight: 'bold' }}>↗ 連續上升</span>;
      } else if (opmIsFalling) {
        return <span style={{ color: '#f5222d', fontWeight: 'bold' }}>↘ 連續下降</span>;
      } else {
        return <span style={{ color: '#faad14', fontWeight: 'bold' }}>→ 非連續</span>;
      }
    }
  },
  {
    title: () => <span style={{ color: 'white' }}>最新淨利率</span>,
    dataIndex: 'npm1',
    key: 'npm1',
    width: 100,
    render: (value) => value ? `${(parseFloat(value) * 100).toFixed(2)}%` : '-'
  },
  {
    title: () => <span style={{ color: 'white' }}>一季前淨利率</span>,
    dataIndex: 'npm2',
    key: 'npm2',
    width: 100,
    render: (value) => value ? `${(parseFloat(value) * 100).toFixed(2)}%` : '-'
  },
  {
    title: () => <span style={{ color: 'white' }}>兩季前淨利率</span>,
    dataIndex: 'npm3',
    key: 'npm3',
    width: 100,
    render: (value) => value ? `${(parseFloat(value) * 100).toFixed(2)}%` : '-'
  },
  {
    title: () => <span style={{ color: 'white' }}>三季前淨利率</span>,
    dataIndex: 'npm4',
    key: 'npm4',
    width: 100,
    render: (value) => value ? `${(parseFloat(value) * 100).toFixed(2)}%` : '-'
  },
  {
    title: () => <span style={{ color: 'white' }}>淨利率趨勢</span>,
    key: 'npm_trend',
    width: 120,
    render: (text, record) => {
      const npm1 = record.npm1;
      const npm2 = record.npm2;
      const npm3 = record.npm3;
      const npm4 = record.npm4;
      
      if (!npm1 || !npm2 || !npm3 || !npm4) return '-';
      
      const npmIsRising = npm4 < npm3 && npm3 < npm2 && npm2 < npm1;
      const npmIsFalling = npm4 > npm3 && npm3 > npm2 && npm2 > npm1;
      
      if (npmIsRising) {
        return <span style={{ color: '#52c41a', fontWeight: 'bold' }}>↗ 連續上升</span>;
      } else if (npmIsFalling) {
        return <span style={{ color: '#f5222d', fontWeight: 'bold' }}>↘ 連續下降</span>;
      } else {
        return <span style={{ color: '#faad14', fontWeight: 'bold' }}>→ 非連續</span>;
      }
    }
  },
  {
    title: () => <span style={{ color: 'white' }}>最新營收</span>,
    dataIndex: 'revenue_q1',
    key: 'revenue_q1',
    width: 100,
    render: (value) => {
      if (!value) return '-';
      const numValue = parseFloat(value);
      if (numValue >= 1000000000) {
        return `$${(numValue / 1000000000).toFixed(2)}B`;
      } else {
        return `$${(numValue / 1000000).toFixed(2)}M`;
      }
    }
  },
  {
    title: () => <span style={{ color: 'white' }}>一季前營收</span>,
    dataIndex: 'revenue_q2',
    key: 'revenue_q2',
    width: 100,
    render: (value) => {
      if (!value) return '-';
      const numValue = parseFloat(value);
      if (numValue >= 1000000000) {
        return `$${(numValue / 1000000000).toFixed(2)}B`;
      } else {
        return `$${(numValue / 1000000).toFixed(2)}M`;
      }
    }
  },
  {
    title: () => <span style={{ color: 'white' }}>兩季前營收</span>,
    dataIndex: 'revenue_q3',
    key: 'revenue_q3',
    width: 100,
    render: (value) => {
      if (!value) return '-';
      const numValue = parseFloat(value);
      if (numValue >= 1000000000) {
        return `$${(numValue / 1000000000).toFixed(2)}B`;
      } else {
        return `$${(numValue / 1000000).toFixed(2)}M`;
      }
    }
  },
  {
    title: () => <span style={{ color: 'white' }}>三季前營收</span>,
    dataIndex: 'revenue_q4',
    key: 'revenue_q4',
    width: 100,
    render: (value) => {
      if (!value) return '-';
      const numValue = parseFloat(value);
      if (numValue >= 1000000000) {
        return `$${(numValue / 1000000000).toFixed(2)}B`;
      } else {
        return `$${(numValue / 1000000).toFixed(2)}M`;
      }
    }
  },
  {
    title: () => <span style={{ color: 'white' }}>營收趨勢</span>,
    key: 'revenue_trend',
    width: 120,
    render: (text, record) => {
      const rev1 = record.revenue_q1;
      const rev2 = record.revenue_q2;
      const rev3 = record.revenue_q3;
      const rev4 = record.revenue_q4;
      
      if (!rev1 || !rev2 || !rev3 || !rev4) return '-';
      
      const revIsRising = rev4 < rev3 && rev3 < rev2 && rev2 < rev1;
      const revIsFalling = rev4 > rev3 && rev3 > rev2 && rev2 > rev1;
      
      if (revIsRising) {
        return <span style={{ color: '#52c41a', fontWeight: 'bold' }}>↗ 連續上升</span>;
      } else if (revIsFalling) {
        return <span style={{ color: '#f5222d', fontWeight: 'bold' }}>↘ 連續下降</span>;
      } else {
        return <span style={{ color: '#faad14', fontWeight: 'bold' }}>→ 非連續</span>;
      }
    }
  },
  {
    title: () => <span style={{ color: 'white' }}>最新EPS</span>,
    dataIndex: 'eps_q1',
    key: 'eps_q1',
    width: 80,
    render: (value) => value ? `$${parseFloat(value).toFixed(2)}` : '-'
  },
  {
    title: () => <span style={{ color: 'white' }}>一季前EPS</span>,
    dataIndex: 'eps_q2',
    key: 'eps_q2',
    width: 80,
    render: (value) => value ? `$${parseFloat(value).toFixed(2)}` : '-'
  },
  {
    title: () => <span style={{ color: 'white' }}>兩季前EPS</span>,
    dataIndex: 'eps_q3',
    key: 'eps_q3',
    width: 80,
    render: (value) => value ? `$${parseFloat(value).toFixed(2)}` : '-'
  },
  {
    title: () => <span style={{ color: 'white' }}>三季前EPS</span>,
    dataIndex: 'eps_q4',
    key: 'eps_q4',
    width: 80,
    render: (value) => value ? `$${parseFloat(value).toFixed(2)}` : '-'
  },
  {
    title: () => <span style={{ color: 'white' }}>EPS趨勢</span>,
    key: 'eps_trend',
    width: 120,
    render: (text, record) => {
      const eps1 = record.eps_q1;
      const eps2 = record.eps_q2;
      const eps3 = record.eps_q3;
      const eps4 = record.eps_q4;
      
      if (!eps1 || !eps2 || !eps3 || !eps4) return '-';
      
      // 修复EPS趋势判断逻辑，正确处理负数
      // 连续上升：eps4 < eps3 < eps2 < eps1 (包括负数情况)
      // 连续下降：eps4 > eps3 > eps2 > eps1 (包括负数情况)
      const epsIsRising = parseFloat(eps4) < parseFloat(eps3) && 
                         parseFloat(eps3) < parseFloat(eps2) && 
                         parseFloat(eps2) < parseFloat(eps1);
      const epsIsFalling = parseFloat(eps4) > parseFloat(eps3) && 
                          parseFloat(eps3) > parseFloat(eps2) && 
                          parseFloat(eps2) > parseFloat(eps1);
      
      if (epsIsRising) {
        return <span style={{ color: '#52c41a', fontWeight: 'bold' }}>↗ 連續上升</span>;
      } else if (epsIsFalling) {
        return <span style={{ color: '#f5222d', fontWeight: 'bold' }}>↘ 連續下降</span>;
      } else {
        return <span style={{ color: '#faad14', fontWeight: 'bold' }}>→ 非連續</span>;
      }
    }
  },
  {
    title: () => <span style={{ color: 'white' }}>股價</span>,
    dataIndex: 'price',
    key: 'price',
    width: 80,
    render: (value) => value ? `$${parseFloat(value).toFixed(2)}` : '-'
  },
  {
    title: () => <span style={{ color: 'white' }}>漲跌幅</span>,
    dataIndex: 'changesPercentage',
    key: 'changesPercentage',
    width: 80,
    render: (value) => {
      if (!value) return '-';
      const numValue = parseFloat(value);
      const color = numValue >= 0 ? '#52c41a' : '#ff4d4f';
      return <span style={{ color }}>{numValue >= 0 ? '+' : ''}{numValue.toFixed(2)}%</span>;
    }
  }
];

// 操作列
export const actionColumn = {
  title: '操作',
  key: 'action',
  width: 120,
  fixed: 'right',
  render: (_, record) => {
    // 這個 render 函數需要在具體組件中實現
    return null;
  }
};

// 導出所有列定義
export const allColumns = {
  basic: basicInfoColumns,
  growth: growthValuationColumns,
  financial: financialRatiosColumns,
  price: pricePerformanceColumns,
  trend: trendColumns
};

// 獲取指定視圖類型的列定義
export const getColumnsByType = (viewType, customActionColumn = null, excludeWatchlist = false) => {
  let baseColumns = allColumns[viewType] || basicInfoColumns;
  
  // 如果排除watchlist列（用於WatchlistPage），則移除watchlist操作列
  if (excludeWatchlist) {
    baseColumns = baseColumns.filter(col => col.key !== 'watchlist_action');
  }
  
  const actionCol = customActionColumn || actionColumn;
  return [...baseColumns, actionCol];
};

// 分頁名稱定義
export const pageNames = {
  basic: '基本訊息',
  growth: '估值成長',
  financial: '財務比率',
  price: '價格表現',
  trend: '經營趨勢',
  technical: '技術圖表'
};

// 分頁圖標定義
export const pageIcons = {
  basic: 'AppstoreOutlined',
  growth: 'LineChartOutlined',
  financial: 'BarChartOutlined',
  price: 'StockOutlined',
  trend: 'DollarOutlined',
  technical: 'BarChartOutlined'
};

// 分頁順序定義
export const pageOrder = ['basic', 'growth', 'financial', 'price', 'trend', 'technical']; 